import { useState } from 'react'
import { motion } from 'framer-motion'
import { 
  Settings as SettingsIcon,
  Key,
  Info,
  Copy,
  Check,
  XCircle,
  AlertTriangle,
  ExternalLink,
  Github,
  Mail,
  Shield,
  Zap,
  Palette,
  Globe,
  Database,
  Sparkles,
  RefreshCw,
  Bell,
  Moon,
  Sun,
  Monitor,
  ArrowUpRight
} from 'lucide-react'
import { useApi } from '../contexts/ApiContext'
import { useTheme } from '../contexts/ThemeContext'
import { ThemeIndicator } from '../components/ThemeIndicator'
import { VersionDisplay } from '../components/VersionDisplay'
import { getCurrentVersionInfo } from '../lib/version'
import { cn } from '../lib/utils'

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
}

export default function Settings() {
  const { isAuthenticated } = useApi()
  const { theme, setTheme, resolvedTheme } = useTheme()
  const [copied, setCopied] = useState(false)
  const currentVersion = getCurrentVersionInfo()

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy text: ', err)
    }
  }

  const features = [
    {
      name: 'Real-time Stock Tracking',
      description: 'Live updates of all available items with rarity information',
      icon: Database,
      color: 'text-info',
      bgColor: 'bg-info/10',
      borderColor: 'border-info/30'
    },
    {
      name: 'Weather Monitoring',
      description: 'Track weather conditions and their effects on farming',
      icon: Globe,
      color: 'text-success',
      bgColor: 'bg-success/10',
      borderColor: 'border-success/30'
    },
    {
      name: 'Game Guide System',
      description: 'Comprehensive guides and strategies for optimal gameplay',
      icon: Palette,
      color: 'text-purple',
      bgColor: 'bg-purple/10',
      borderColor: 'border-purple/30'
    },
    {
      name: 'Market Insights',
      description: 'Analyze market trends and item values',
      icon: Zap,
      color: 'text-orange',
      bgColor: 'bg-orange/10',
      borderColor: 'border-orange/30'
    }
  ]

  const supportLinks = [
    {
      name: 'GitHub Repository',
      description: 'View source code and contribute',
      icon: Github,
      href: 'https://github.com/juleskuy/',
      color: 'text-foreground',
      bgColor: 'bg-muted/50',
      borderColor: 'border-border/30'
    },
    {
      name: 'API Documentation',
      description: 'Learn about the Grow a Garden API',
      icon: ExternalLink,
      href: 'https://api.joshlei.com/docs/',
      color: 'text-info',
      bgColor: 'bg-info/10',
      borderColor: 'border-info/30'
    },
    {
      name: 'Report Issues',
      description: 'Submit bugs and feature requests',
      icon: Mail,
      href: 'mailto:zulfann22@gmail.com',
      color: 'text-success',
      bgColor: 'bg-success/10',
      borderColor: 'border-success/30'
    }
  ]

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-modern"
    >
      <div className="space-modern-sm">
        <div className="flex items-center space-x-3 mb-4">
          <div className="p-3 rounded-2xl bg-gradient-to-r from-pink to-pink/80 shadow-lg">
            <SettingsIcon className="h-6 w-6 text-primary-foreground" />
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-foreground">
              Settings
            </h1>
            <p className="text-muted-foreground leading-relaxed">
              Configure your API key and explore application features
            </p>
          </div>
        </div>
      </div>

      <div className="grid-modern lg:grid-cols-3">
        {/* Enhanced API Configuration */}
        <motion.div variants={itemVariants} className="gaming-card lg:col-span-2">
          <div className="card-header">
            <div className="flex items-center space-x-3">
              <div className="p-3 rounded-xl bg-gradient-to-r from-info/20 to-info/10 border border-info/30">
                <Key className="h-6 w-6 text-info" />
              </div>
              <h3 className="card-title">API Configuration</h3>
            </div>
          </div>
          <div className="card-content space-modern-sm">
            {isAuthenticated ? (
              <div className="space-y-4">
                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="flex items-center space-x-4 p-4 glass rounded-xl border border-border/30"
                >
                  <div className="p-2 rounded-xl bg-success/10 border border-success/30">
                    <Check className="h-5 w-5 text-success" />
                  </div>
                  <div>
                    <p className="font-semibold text-success">API Key Configured</p>
                    <p className="text-sm text-muted-foreground">Your API key is working correctly</p>
                  </div>
                </motion.div>
                
                <div className="p-4 glass rounded-xl border border-border/30">
                  <p className="text-sm font-medium mb-3">API Key Status</p>
                  <div className="flex items-center space-x-3">
                    <code className="flex-1 bg-muted/50 px-4 py-3 rounded-xl border border-border/30 text-sm font-mono">
                      Configured and working
                    </code>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => copyToClipboard('API key is configured')}
                      className="btn btn-outline btn-md"
                    >
                      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </motion.button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="flex items-center space-x-4 p-4 glass rounded-xl border border-border/30"
                >
                  <div className="p-2 rounded-xl bg-destructive/10 border border-destructive/30">
                    <XCircle className="h-5 w-5 text-destructive" />
                  </div>
                  <div>
                    <p className="font-semibold text-destructive">API Key Required</p>
                    <p className="text-sm text-muted-foreground">Configure your API key to access features</p>
                  </div>
                </motion.div>

                <div className="glass rounded-xl border border-border/30 p-6">
                  <h4 className="font-semibold mb-4">Setup Instructions</h4>
                  <ol className="text-sm text-muted-foreground space-y-3 mb-6">
                    <li className="flex items-center space-x-3">
                      <span className="w-6 h-6 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center text-xs font-bold">1</span>
                      <span>Create a <code className="bg-muted px-2 py-1 rounded-lg border border-border/30">.env</code> file in the project root</span>
                    </li>
                    <li className="flex items-center space-x-3">
                      <span className="w-6 h-6 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center text-xs font-bold">2</span>
                      <span>Add your API key: <code className="bg-muted px-2 py-1 rounded-lg border border-border/30">VITE_API_KEY=your_api_key_here</code></span>
                    </li>
                    <li className="flex items-center space-x-3">
                      <span className="w-6 h-6 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center text-xs font-bold">3</span>
                      <span>Restart the development server</span>
                    </li>
                  </ol>
                  
                  <div className="p-4 bg-muted/50 rounded-xl border border-border/30">
                    <p className="text-sm font-medium mb-3">Example .env file:</p>
                    <div className="relative">
                      <pre className="text-xs bg-background p-4 rounded-xl border border-border/30 overflow-x-auto">
                        <code>VITE_API_KEY=your_grow_a_garden_api_key_here</code>
                      </pre>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => copyToClipboard('VITE_API_KEY=your_grow_a_garden_api_key_here')}
                        className="absolute top-3 right-3 btn btn-sm btn-outline"
                      >
                        {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                      </motion.button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>

        {/* Enhanced Application Info */}
        <motion.div variants={itemVariants} className="gaming-card">
          <div className="card-header">
            <div className="flex items-center space-x-3">
              <div className="p-3 rounded-xl bg-gradient-to-r from-success/20 to-success/10 border border-success/30">
                <Info className="h-6 w-6 text-success" />
              </div>
              <h3 className="card-title">Application Info</h3>
            </div>
          </div>
          <div className="card-content space-modern-sm">
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 glass rounded-xl border border-border/30 hover:bg-muted/20 transition-modern">
                <div className="flex items-center space-x-3">
                  <div className="p-2 rounded-lg bg-muted/50 border border-border/30">
                    <Shield className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <span className="text-sm font-medium">Version</span>
                </div>
                                 <span className="font-bold">{currentVersion.version}</span>
              </div>
              
              <div className="flex items-center justify-between p-3 glass rounded-xl border border-border/30 hover:bg-muted/20 transition-modern">
                <div className="flex items-center space-x-3">
                  <div className="p-2 rounded-lg bg-muted/50 border border-border/30">
                    <Zap className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <span className="text-sm font-medium">Status</span>
                </div>
                <span className={cn("font-bold", isAuthenticated ? "text-success" : "text-destructive")}>
                  {isAuthenticated ? "Connected" : "Disconnected"}
                </span>
              </div>
              
              <div className="flex items-center justify-between p-3 glass rounded-xl border border-border/30 hover:bg-muted/20 transition-modern">
                <div className="flex items-center space-x-3">
                  <div className="p-2 rounded-lg bg-muted/50 border border-border/30">
                    <Database className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <span className="text-sm font-medium">API Status</span>
                </div>
                <span className={cn("font-bold", isAuthenticated ? "text-success" : "text-destructive")}>
                  {isAuthenticated ? "Online" : "Offline"}
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Enhanced Features */}
        <motion.div variants={itemVariants} className="gaming-card">
          <div className="card-header">
            <div className="flex items-center space-x-3">
              <div className="p-3 rounded-xl bg-gradient-to-r from-orange/20 to-orange/10 border border-orange/30">
                <Zap className="h-6 w-6 text-orange" />
              </div>
              <h3 className="card-title">Features</h3>
            </div>
          </div>
          <div className="card-content">
            <div className="space-y-3">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  whileHover={{ scale: 1.02 }}
                  className="p-3 glass rounded-xl border border-border/30 transition-modern hover:bg-muted/20"
                >
                  <div className="flex items-center space-x-3">
                    <div className={cn("p-2 rounded-lg border", feature.bgColor, feature.borderColor)}>
                      <feature.icon className={cn("h-4 w-4", feature.color)} />
                    </div>
                    <div>
                      <p className="font-semibold text-sm">{feature.name}</p>
                      <p className="text-xs text-muted-foreground">{feature.description}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Enhanced Support & Resources */}
        <motion.div variants={itemVariants} className="gaming-card">
          <div className="card-header">
            <div className="flex items-center space-x-3">
              <div className="p-3 rounded-xl bg-gradient-to-r from-purple/20 to-purple/10 border border-purple/30">
                <ExternalLink className="h-6 w-6 text-purple" />
              </div>
              <h3 className="card-title">Support & Resources</h3>
            </div>
          </div>
          <div className="card-content">
            <div className="space-y-3">
              {supportLinks.map((link, index) => (
                <motion.a
                  key={index}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.02 }}
                  className="p-3 glass rounded-xl border border-border/30 transition-modern hover:bg-muted/20 cursor-pointer block"
                >
                  <div className="flex items-center space-x-3">
                    <div className={cn("p-2 rounded-lg border", link.bgColor, link.borderColor)}>
                      <link.icon className={cn("h-4 w-4", link.color)} />
                    </div>
                    <div>
                      <p className="font-semibold text-sm">{link.name}</p>
                      <p className="text-xs text-muted-foreground">{link.description}</p>
                    </div>
                  </div>
                </motion.a>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Enhanced Preferences */}
        <motion.div variants={itemVariants} className="gaming-card">
          <div className="card-header">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-3 rounded-xl bg-gradient-to-r from-info/20 to-info/10 border border-info/30">
                  <Bell className="h-6 w-6 text-info" />
                </div>
                <h3 className="card-title">Preferences</h3>
              </div>
              <ThemeIndicator />
            </div>
          </div>
          <div className="card-content">
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 glass rounded-xl border border-border/30 hover:bg-muted/20 transition-modern">
                <div className="flex items-center space-x-3">
                  <div className="p-2 rounded-lg bg-muted/50 border border-border/30">
                    <Bell className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <span className="text-sm font-medium">Notifications</span>
                </div>
                <div className="w-12 h-6 bg-muted rounded-full relative cursor-pointer">
                  <div className="w-5 h-5 bg-primary rounded-full absolute top-0.5 left-0.5 transition-transform"></div>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-3 glass rounded-xl border border-border/30 hover:bg-muted/20 transition-modern">
                <div className="flex items-center space-x-3">
                  <div className="p-2 rounded-lg bg-muted/50 border border-border/30">
                    <RefreshCw className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <span className="text-sm font-medium">Auto Refresh</span>
                </div>
                <div className="w-12 h-6 bg-muted rounded-full relative cursor-pointer">
                  <div className="w-5 h-5 bg-primary rounded-full absolute top-0.5 left-0.5 transition-transform"></div>
                </div>
              </div>
              
              <div className="space-y-3">
                {[
                  { value: 'system' as const, label: 'System', icon: Monitor, description: 'Follow system preference' },
                  { value: 'light' as const, label: 'Light', icon: Sun, description: 'Light theme' },
                  { value: 'dark' as const, label: 'Dark', icon: Moon, description: 'Dark theme' }
                ].map((option) => {
                  const Icon = option.icon
                  const isActive = theme === option.value
                  
                  return (
                    <motion.button
                      key={option.value}
                      onClick={() => setTheme(option.value)}
                      className={cn(
                        "w-full flex items-center justify-between p-3 glass rounded-xl border transition-all duration-200",
                        "hover:bg-muted/20 focus:outline-none focus:ring-2 focus:ring-primary/20",
                        isActive && "bg-primary/10 border-primary/30"
                      )}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div className="flex items-center space-x-3">
                        <div className={cn(
                          "p-2 rounded-lg border",
                          isActive 
                            ? "bg-primary/20 border-primary/30 text-primary" 
                            : "bg-muted/50 border-border/30 text-muted-foreground"
                        )}>
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="text-left">
                          <div className="text-sm font-medium">{option.label}</div>
                          <div className="text-xs text-muted-foreground">{option.description}</div>
                        </div>
                      </div>
                      {isActive && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="w-2 h-2 bg-primary rounded-full"
                        />
                      )}
                    </motion.button>
                  )
                })}
              </div>
            </div>
          </div>
                 </motion.div>
       </div>

       {/* Version Information */}
       <VersionDisplay />

       {/* Enhanced Warning for unauthenticated users */}
      {!isAuthenticated && (
        <motion.div variants={itemVariants} className="gaming-card accent-red">
          <div className="card-content">
            <div className="flex items-start space-x-4">
              <div className="p-3 rounded-xl bg-gradient-to-r from-destructive/20 to-destructive/10 border border-destructive/30">
                <AlertTriangle className="h-6 w-6 text-destructive" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-destructive mb-2">Authentication Required</h3>
                <p className="text-destructive/90 text-sm leading-relaxed">
                  Most features require a valid API key. Please configure your API key to access stock tracking, weather monitoring, and other features.
                </p>
              </div>
              <div className="p-2 rounded-lg bg-destructive/10">
                <ArrowUpRight className="h-4 w-4 text-destructive" />
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </motion.div>
  )
} 